import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export const PUBLISHED_COURSES_PAGE_SIZE = 10;

export type PublishedCourseRow = {
	course_id: string;
	course_name: string;
};

/** Escape % and _ so user input cannot broaden an ILIKE pattern. */
function sanitizeIlikeSearch(raw: string): string {
	return raw.trim().replace(/[%_]/g, '');
}

export function usePublishedCourses(params: {
	searchTerm: string;
	page: number;
	currentCourseId: string;
	enabled?: boolean;
}) {
	const { searchTerm, page, currentCourseId, enabled = true } = params;
	const debouncedOrRaw = searchTerm; // caller debounces before passing

	return useQuery({
		queryKey: ['published-courses-for-pull', schoolId, currentCourseId, debouncedOrRaw, page],
		queryFn: async () => {
			const safeSearch = sanitizeIlikeSearch(debouncedOrRaw);
			const from = page * PUBLISHED_COURSES_PAGE_SIZE;
			const to = from + PUBLISHED_COURSES_PAGE_SIZE - 1;

			let query = supabase
				.from('courses')
				.select(
					`
					course_id,
					course_name
				`,
					{ count: 'exact' }
				)
				.eq('school_id', schoolId)
				.neq('course_id', currentCourseId)
				.or('is_archived.eq.false,is_archived.is.null');

			if (safeSearch.length > 0) {
				query = query.ilike('course_name', `%${safeSearch}%`);
			}

			const { data, error, count } = await query
				.order('course_name', { ascending: true })
				.range(from, to);

			if (error) throw error;

			const courses: PublishedCourseRow[] =
				(data ?? []).map((row: { course_id: string; course_name: string }) => ({
					course_id: row.course_id,
					course_name: row.course_name,
				})) ?? [];

			return {
				courses,
				totalCount: count ?? 0,
			};
		},
		enabled: enabled && Boolean(schoolId) && Boolean(currentCourseId),
		staleTime: 30_000,
	});
}
